import { cn } from "@faire/react/tools/cn";
import type { IText } from "@faire/slate/private/shared/types";
import React from "react";

import { FormHelper, FormLabel } from "./formTexts";
import { TooltipFormLabel } from "./TooltipFormLabel";

type IProps = React.PropsWithChildren<{
  /**
   * The id of the input.
   */
  inputId?: string;

  /**
   * The id of the helper.
   */
  helperId?: string;

  /**
   * The label of the input.
   */
  label?: React.ReactNode;

  /**
   * Whether to append "(optional)" text to the label.
   */
  optional: boolean;

  /**
   * The helper text of the input.
   */
  helper?: React.ReactNode;

  /**
   * The tooltip text for the tooltip in the label.
   */
  tooltipText?: IText;

  /**
   * The z-index of the label tooltip.
   *
   * @default 5
   */
  tooltipZIndex?: number;

  /**
   * The data component name. Used for adoption tracking.
   */
  "data-component-name"?: string;
}> &
  React.ComponentProps<typeof FormLabel> &
  React.ComponentProps<typeof FormHelper>;

export const Labelled: React.FC<IProps> = ({
  children,
  className,
  disabled,
  error,
  gap,
  helper,
  helperId,
  label,
  inputId,
  optional,
  tooltipText,
  tooltipZIndex,
  "data-component-name": dataComponentName,
}) => {
  return (
    <div
      className={cn("flex w-full flex-col", className)}
      data-component-name={dataComponentName}
    >
      <FormLabel
        disabled={disabled}
        error={error}
        gap={gap}
        htmlFor={inputId}
        optional={optional}
        tooltip={
          tooltipText ? (
            <TooltipFormLabel text={tooltipText} zIndex={tooltipZIndex} />
          ) : null
        }
      >
        {label}
      </FormLabel>
      {children}
      <FormHelper disabled={disabled} error={error} gap={gap} id={helperId}>
        {helper}
      </FormHelper>
    </div>
  );
};
